import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';

interface Product {
  id: string;
  name: string;
  price: number;
  images: { url: string; alt: string }[];
  description: string;
  stock: number;
  category: string;
  isExclusivePreview?: boolean;
}

export default function ProduitQuatreCoeursLivrePlie() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);

  const product: Product = {
    id: 'quatre-coeurs-livre-plie',
    name: 'Livre plié "Quatre cœurs" – 3 petits cœurs & 1 grand (Pièce unique)',
    price: 30,
    images: [
      {
        url: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/0b9aeda089d6f184b1d55f54e41ff5cb.png',
        alt: 'Livre plié Quatre cœurs - 3 petits et 1 grand'
      }
    ],
    description: 'Une création en livre plié représentant 4 cœurs en relief : trois petits à gauche et un grand à droite. Pièce unique, tendre et symbolique.',
    stock: 1,
    category: 'Avant-première',
    isExclusivePreview: true
  };

  useEffect(() => {
    // Titre de la page
    document.title = 'Livre plié Quatre cœurs – Pièce unique artisanale | Les Carnets de Lolotte';
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Livre plié avec 4 cœurs en relief (3 petits + 1 grand). Création artisanale unique en livre recyclé. Cadeau romantique et symbolique.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Livre plié avec 4 cœurs en relief (3 petits + 1 grand). Création artisanale unique en livre recyclé. Cadeau romantique et symbolique.';
      document.head.appendChild(meta);
    }

    // Meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'livre plié coeurs, quatre coeurs, decoration romantique, cadeau couple, creation artisanale, piece unique');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'livre plié coeurs, quatre coeurs, decoration romantique, cadeau couple, creation artisanale, piece unique';
      document.head.appendChild(meta);
    }

    // Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Livre plié Quatre cœurs – Pièce unique artisanale | Les Carnets de Lolotte');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Livre plié avec 4 cœurs en relief (3 petits + 1 grand). Création artisanale unique en livre recyclé. Cadeau romantique et symbolique.');
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', product.images[0].url);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', window.location.href);
    }

    // JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      image: product.images[0].url,
      offers: {
        '@type': 'Offer',
        price: product.price,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url: window.location.href
      }
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [product]);

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      quantity,
      image: product.images[0].url
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/panier');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] to-white">
      {/* Notification */}
      {showNotification && (
        <div className="fixed top-24 right-4 bg-[#B97A72] text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-xl"></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>Produit ajouté au panier !</span>
          </div>
        </div>
      )}

      {/* Fil d'Ariane */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ol className="flex items-center gap-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
          <li>
            <button onClick={() => navigate('/')} className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors cursor-pointer">
              Accueil
            </button>
          </li>
          <li className="text-[#C49B86]">
            <i className="ri-arrow-right-s-line"></i>
          </li>
          <li>
            <button onClick={() => navigate('/avant-premiere')} className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors cursor-pointer">
              Avant-première
            </button>
          </li>
          <li className="text-[#C49B86]">
            <i className="ri-arrow-right-s-line"></i>
          </li>
          <li className="text-[#B97A72] font-medium">Livre plié "Quatre cœurs"</li>
        </ol>
      </nav>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Galerie d'images */}
          <div className="space-y-4">
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg border-2 border-[#F5E6E0] w-full h-[500px]">
              <img
                src={product.images[0].url}
                alt={product.images[0].alt}
                className="w-full h-full object-contain object-center"
              />
            </div>
          </div>

          {/* Informations produit */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-pink-100 to-rose-100 text-[#B97A72] text-sm font-medium rounded-full border border-[#B97A72]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <i className="ri-star-line"></i>
                Pièce unique
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-sm font-medium rounded-full border border-purple-300" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <i className="ri-gift-line"></i>
                Avant-première
              </span>
            </div>

            {/* Titre */}
            <h1 className="text-3xl sm:text-4xl font-bold text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {product.name}
            </h1>

            {/* Prix */}
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {product.price} €
              </span>
            </div>

            {/* Description courte */}
            <p className="text-base text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {product.description}
            </p>

            {/* Stock */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                En stock (dernière pièce)
              </span>
            </div>

            {/* Quantité */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Quantité
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#C49B86] text-[#6B5E5A] rounded-lg hover:bg-[#FFF5F0] transition-colors cursor-pointer"
                  disabled={quantity <= 1}
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="number"
                  min="1"
                  max={product.stock}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.min(product.stock, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-20 h-10 text-center border-2 border-[#C49B86] rounded-lg text-[#6B5E5A] font-medium"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                />
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-[#C49B86] text-[#6B5E5A] rounded-lg hover:bg-[#FFF5F0] transition-colors cursor-pointer"
                  disabled={quantity >= product.stock}
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-[#B97A72] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#A66A64] transition-colors shadow-md whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Ajouter au panier
              </button>
              <button
                onClick={handleBuyNow}
                className="flex-1 bg-[#C49B86] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#B38A75] transition-colors shadow-md whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Acheter maintenant
              </button>
            </div>

            {/* Caractéristiques */}
            <div className="bg-[#FFF5F0] rounded-xl p-6 space-y-3 border border-[#F5E6E0]">
              <h3 className="text-lg font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Caractéristiques
              </h3>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#B97A72] mt-0.5"></i>
                  <span className="text-[#6B5E5A]"><strong>Type :</strong> Sculpture en livre plié</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#B97A72] mt-0.5"></i>
                  <span className="text-[#6B5E5A]"><strong>Motif :</strong> 4 cœurs en relief (3 petits à gauche + 1 grand à droite)</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#B97A72] mt-0.5"></i>
                  <span className="text-[#6B5E5A]"><strong>Décor :</strong> Éléments papier décoratifs</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#B97A72] mt-0.5"></i>
                  <span className="text-[#6B5E5A]"><strong>Pièce :</strong> Unique (stock 1)</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#B97A72] mt-0.5"></i>
                  <span className="text-[#6B5E5A]"><strong>Dimensions :</strong> à renseigner</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description détaillée */}
        <div className="mt-12 max-w-4xl mx-auto space-y-8">
          {/* Une composition poétique */}
          <section className="bg-white rounded-2xl p-8 shadow-lg border border-[#F5E6E0]">
            <h2 className="text-2xl font-bold text-[#C49B86] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <i className="ri-heart-3-line text-[#B97A72]"></i>
              Une composition poétique
            </h2>
            <div className="prose prose-lg max-w-none text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p className="leading-relaxed">
                Cette pièce a été réalisée à la main à partir d'un livre plié avec précision pour faire apparaître une composition de cœurs en volume : <strong>3 petits cœurs à gauche et 1 grand cœur à droite</strong>.
              </p>
              <p className="leading-relaxed">
                Un objet décoratif romantique et poétique, parfait pour un cadeau plein de sens (couple, famille, lien, amour).
              </p>
              <ul className="space-y-2 mt-4">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#B97A72] mt-1"></i>
                  <span>✅ Fait main</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#B97A72] mt-1"></i>
                  <span>♻️ Livre recyclé (upcycling)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#B97A72] mt-1"></i>
                  <span>🎁 Emballage soigné, prêt à offrir</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pourquoi on l'aime */}
          <section className="bg-gradient-to-br from-[#FFF5F0] to-white rounded-2xl p-8 shadow-lg border border-[#F5E6E0]">
            <h2 className="text-2xl font-bold text-[#C49B86] mb-6 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <i className="ri-heart-fill text-[#B97A72]"></i>
              Pourquoi on l'aime
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-white rounded-full shadow-md">
                  <i className="ri-star-line text-3xl text-[#B97A72]"></i>
                </div>
                <h3 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Création unique
                </h3>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Une seule pièce au monde, façonnée à la main avec soin
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-white rounded-full shadow-md">
                  <i className="ri-heart-3-line text-3xl text-[#B97A72]"></i>
                </div>
                <h3 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Symbolique forte
                </h3>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  4 cœurs pour représenter l'amour, la famille, les liens
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-white rounded-full shadow-md">
                  <i className="ri-leaf-line text-3xl text-[#B97A72]"></i>
                </div>
                <h3 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Cadeau responsable
                </h3>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Livre recyclé, création artisanale et durable
                </p>
              </div>
            </div>
          </section>

          {/* Points forts */}
          <section className="bg-white rounded-2xl p-8 shadow-lg border border-[#F5E6E0]">
            <h2 className="text-2xl font-bold text-[#C49B86] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Points forts
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-[#FFF5F0] rounded-lg">
                <i className="ri-hand-heart-line text-2xl text-[#B97A72]"></i>
                <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  ✋ Création artisanale faite main
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#FFF5F0] rounded-lg">
                <i className="ri-vip-crown-line text-2xl text-[#B97A72]"></i>
                <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  👑 Pièce unique (1 exemplaire)
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#FFF5F0] rounded-lg">
                <i className="ri-heart-3-line text-2xl text-[#B97A72]"></i>
                <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  💕 Motif 4 cœurs (3 + 1)
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#FFF5F0] rounded-lg">
                <i className="ri-flower-line text-2xl text-[#B97A72]"></i>
                <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  🌸 Déco poétique
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#FFF5F0] rounded-lg">
                <i className="ri-gift-line text-2xl text-[#B97A72]"></i>
                <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  🎁 Cadeau symbolique
                </span>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}